/**
 * Author: Sri Pooja
 * Description: This file contains Redux actions for various cases and their purposes.
 * Each action is a function that returns an object with a 'type' property and additional payload data.
 * These actions are used to update the state in Redux store and trigger corresponding changes in the application.
 */
import { getEnvVariable } from "../../modules/environmentalList";
import { PostData } from "../controls/helpers/requests";
import { GetFetchProps, getToken } from "../modules/header"

const SHOW_POPUP = 'SHOW_POPUP';
const HIDE_POPUP = 'HIDE_POPUP';
export const SHOW_ALERT = 'SHOW_ALERT';
export const HIDE_ALERT = 'HIDE_ALERT';
export const NOTIFICATIONS = 'NOTIFICATIONS';
export const SNACKBAR_MESSAGE = 'SNACKBAR_MESSAGE';
export const OPEN_SNACKBAR = 'OPEN_SNACKBAR';
export const SNACKBAR_MESSAGE_TYPE = 'SNACKBAR_MESSAGE_TYPE';
export const PROJMESSAGES = 'PROJMESSAGES';
export const PASS_COMPMAP = 'PASS_COMPMAP';
export const OPEN_TOUR = 'OPEN_TOUR';
export const CLOSE_TOUR = 'CLOSE_TOUR'
export const WORKFLOW_RELOAD = 'WORKFLOW_RELOAD';
export const WORKFLOW_APPROVER = 'WORKFLOW_APPROVER';
export const UNSAVED_RESULTS = 'UNSAVED_RESULTS';
export const UNSAVED_DATA = 'UNSAVED_DATA';
export const PASSTAB_DATA = 'PASSTAB_DATA';
export const RECORD_DATA = 'RECORD_DATA';
export const RESET_VALUES = 'RESET_VALUES';
export const CONFIRMATION = 'CONFIRMATION';
export const BUSINESS_RULES_ONEVENT = 'BUSINESS_RULES_ONEVENT';
export const ONEVENT_STATUS_TRANS = 'ONEVENT_STATUS_TRANS';
export const ONEVENT_SHOW_ERRORS = 'ONEVENT_SHOW_ERRORS'
export const TRIGGER_AFTER_SAVE = 'TRIGGER_AFTER_SAVE'
export const LEFTMENUDRAWER = 'LEFTMENUDRAWER';
export const MOVEBARTOTOP = 'MOVEBARTOTOP';
export const SITEPERMITTYPE_STATUS = 'SITEPERMITTYPE_STATUS'
export const SAVEWORKFLOWSTATUS = 'SAVEWORKFLOWSTATUS';
export const EXTRAPROPS_WORKFLOW = 'EXTRAPROPS_WORKFLOW'
export const RUNHOLDVALS = 'RUNHOLDVALS'
export const RUNAFTERPROMISECORE = 'RUNAFTERPROMISECORE'
export const SELECTED_GRID_ROWS = 'SELECTED_GRID_ROWS'
export const SHOW_UNSAVE_ALERT = 'SHOW_UNSAVE_ALERT'
/**
 * Redux action function to show a popup.
 * @param {string} control - The control to show in the popup.
 * @param {boolean} force - Whether to force show the popup.
 * @returns {object} - The action object with type 'SHOW_POPUP', control, and force.
 */
export function ShowPopUp(control, force = false) {
    return { type: SHOW_POPUP, control, force };
}

/**
 * Redux action function to hide the popup.
 * @returns {object} - The action object with type 'HIDE_POPUP'.
 */
export function HidePopUp() {
    return { type: HIDE_POPUP };
}

/**
 * Redux action function to show an alert.
 * @param {string} alerttype - The type of alert.
 * @param {string} alertmessage - The message of the alert.
 * @returns {object} - The action object with type 'SHOW_ALERT', alerttype, and alertmessage.
 */
export const showAlert = (alerttype, alertmessage) => {
    return {
        type: SHOW_ALERT,
        alerttype: alerttype,
        alertmessage: alertmessage
    }
}

/**
 * Redux action function to hide the alert.
 * @param {object} alert - The alert object.
 * @returns {object} - The action object with type 'HIDE_ALERT'.
 */
export const hideAlert = (alert) => {
    return {
        type: HIDE_ALERT
    }
}

/**
 * Redux action function to set the notifications.
 * @param {object} notifications - The notifications data.
 * @returns {object} - The action object with type 'NOTIFICATIONS' and notifications.
 */
export const setNotify = (notifications) => {
    return {
        type: NOTIFICATIONS,
        notifications
    }
}

/**
 * Redux action function to set the drawer state.
 * @param {string} drawertype - The type of drawer.
 * @param {string} drawerstate - The state of the drawer.
 * @returns {object} - The action object with type drawertype and drawermessage.
 */
export const DrawerState = (drawertype, drawerstate) => {
    return {
        type: drawertype,
        drawermessage: drawerstate
    }
}

/**
 * Redux action function to set the snackbar message.
 * @param {string} snackmessage - The snack message.
 * @param {string} snackbarmessageType - The type of snackbar message.
 * @returns {object} - The action object with type 'SNACKBAR_MESSAGE', snackmessage, and snackbarmessageType.
 */
export const snackbarMessage = (snackmessage, snackbarmessageType) => {
    return {
        type: SNACKBAR_MESSAGE,
        snackmessage: snackmessage,
        snackbarmessageType: snackbarmessageType
    }
}

/**
 * Redux action function to show a tour.
 * @param {boolean} isTourOpen - Whether the tour is open.
 * @returns {object} - The action object with type 'OPEN_TOUR' and isTourOpen.
 */
export const showTour = (isTourOpen) => {
    return {
        type: OPEN_TOUR,
        isTourOpen: isTourOpen,
    }
}

/**
 * Redux action function to update the onEvent status.
 * @param {boolean} onEventTransition - Whether the onEvent transition is happening.
 * @returns {object} - The action object with type 'ONEVENT_STATUS_TRANS' and onEventTransition.
 */
export const onEventStatusUpdate = (onEventTransition = false) => {
    return {
        type: ONEVENT_STATUS_TRANS,
        onEventTransition: onEventTransition
    }
}

/**
 * Redux action function to save the workflow status.
 * @param {boolean} saveWorkflowStatus - Whether to save the workflow status.
 * @returns {object} - The action object with type 'SAVEWORKFLOWSTATUS' and saveWorkflowStatus.
 */
export const saveWorkflowStatus = (saveWorkflowStatus = false) => {
    return {
        type: SAVEWORKFLOWSTATUS,
        saveWorkflowStatus: saveWorkflowStatus
    }
}

/**
 * Redux action function to set the extra properties for workflow.
 * @param {object} extrapropsworkflow - The extra properties for workflow.
 * @returns {object} - The action object with type 'EXTRAPROPS_WORKFLOW' and extrapropsworkflow.
 */
export const setExtrapropsWorkFlow = (extrapropsworkflow = null) => {
    return {
        type: EXTRAPROPS_WORKFLOW,
        extrapropsworkflow: extrapropsworkflow
    }
}

/**
 * Redux action function to set the SitePermitTypes status.
 * @param {boolean} SitePermitTypesStatus - Whether the SitePermitTypes status is true or false.
 * @returns {object} - The action object with type 'SITEPERMITTYPE_STATUS' and SitePermitTypesStatus.
 */
export const SitePermitTypesStatus = (SitePermitTypesStatus = false) => {
    return {
        type: SITEPERMITTYPE_STATUS,
        SitePermitTypesStatus: SitePermitTypesStatus
    }
}

/**
 * Redux action function to show errors on onEvent.
 * @param {object} onEventshowError - The onEvent show error object.
 * @returns {object} - The action object with type 'ONEVENT_SHOW_ERRORS' and onEventshowError.
 */
export const onEventshowError = (onEventshowError = null) => {
    return {
        type: ONEVENT_SHOW_ERRORS,
        onEventshowError: onEventshowError
    }
}

/**
 * Redux action function to close a tour.
 * @param {boolean} isTourOpen - Whether the tour is open.
 * @returns {object} - The action object with type 'CLOSE_TOUR' and isTourOpen.
 */
export const closeTour = (isTourOpen) => {
    return {
        type: CLOSE_TOUR,
        isTourOpen: isTourOpen,
    }
}

/**
 * Redux action function to handle unsaved data.
 * @param {object} unsaveddata - The unsaved data.
 * @param {object} initialdata - The initial data.
 * @returns {object} - The action object with type 'UNSAVED_DATA', unsaveddata, and initialdata.
 */
export const unsavedData = (unsaveddata, initialdata) => {
    return {
        type: UNSAVED_DATA,
        unsaveddata: { ...unsaveddata },
        initialdata: { ...initialdata }
    }
}

/**
 * Redux action function to show a snackbar.
 * @param {boolean} showUnSaveAlert - Whether the save alert is open.
 * @returns {object} - The action object with type 'SHOW_UNSAVE_ALERT' and opensnackbar.
 * */

export const setShowUnSaveAlert = (showUnSaveAlert) => {
    return {
        type: 'SHOW_UNSAVE_ALERT',
        showUnSaveAlert: showUnSaveAlert
    }
}

/**
 * Redux action function to show a confirmation.
 * @param {function} onyes - The function to execute on 'yes'.
 * @param {function} onno - The function to execute on 'no'.
 * @param {string} description - The description of the confirmation.
 * @param {any} value - The value of the confirmation.
 * @returns {object} - The action object with type 'CONFIRMATION', onyes, onno, description, and value.
 */
export const Confirmation = (onyes, onno, description, value) => {
    return {
        type: CONFIRMATION,
        onyes: onyes,
        onno: onno,
        description: description,
        value: value
    }
}

/**
 * Redux action function to handle unsaved results.
 * @param {object} unsavedresults - The unsaved results.
 * @returns {object} - The action object with type 'UNSAVED_RESULTS' and unsavedresults.
 */
export const unsavedResult = (unsavedresults) => {
    return {
        type: UNSAVED_RESULTS,
        unsavedresults
    }
}

/**
 * Redux action function to set the sections reload.
 * @param {boolean} workflowreload - Whether to reload the workflow sections.
 * @returns {object} - The action object with type 'WORKFLOW_RELOAD' and workflowreload.
 */
export const setSectionsReload = (workflowreload) => {
    return {
        type: WORKFLOW_RELOAD,
        workflowreload
    }
}

/**
 * Redux action function to set the workflow approver.
 * @param {boolean} workflowApprover - Whether the workflow approver is set.
 * @returns {object} - The action object with type 'WORKFLOW_APPROVER' and workflowApprover.
 */
export const setWorkflowApprover = (workflowApprover) => {
    return {
        type: WORKFLOW_APPROVER,
        workflowApprover
    }
}

/**
 * Redux action function to reset values.
 * @param {boolean} isreset - Whether to reset the values.
 * @returns {object} - The action object with type 'RESET_VALUES' and isreset.
 */
export const setResetValue = (isreset) => {
    return {
        type: RESET_VALUES,
        isreset
    }
}

/**
 * Redux action function to set the run hold values.
 * @param {object} runholdvals - The run hold values.
 * @returns {object} - The action object with type 'RUNHOLDVALS' and runholdvals.
 */
export const setRunHoldVals = (runholdvals) => {
    return {
        type: 'RUNHOLDVALS',
        runholdvals
    }
}

/**
 * Redux action function to run after a promise.
 * @param {any} runafterpromise - The value to run after a promise.
 * @returns {object} - The action object with type 'RUNAFTERPROMISECORE' and runafterpromise.
 */
export const runAfterPromise = (runafterpromise) => {
    return {
        type: 'RUNAFTERPROMISECORE',
        runafterpromise
    }
}

/**
 * Redux action function to pass compmap data.
 * @returns {object} - The action object with type 'PASS_COMPMAP'.
 */
export const compmapdata = () => {
    return {
        type: PASS_COMPMAP
    }
}

/**
 * Redux action function to get project messages from the service.
 * @param {object} projmessages - The project messages.
 * @returns {object} - The action object with type 'PROJMESSAGES' and projmessages.
 */
export const getProjMessagesfromService = (projmessages) => {
    return {
        type: PROJMESSAGES,
        projmessages
    }
}

/**
 * Redux action function to set business rules on event.
 * @param {array} effects - The effects for business rules on event.
 * @returns {object} - The action object with type 'BUSINESS_RULES_ONEVENT' and effects.
 */
export const setBusinessRulesOnEvent = (effects = []) => {
    return {
        type: BUSINESS_RULES_ONEVENT,
        effects
    }
}

/**
 * Redux action function to set the record data.
 * @param {object} recordidinfo - The record ID info.
 * @param {object} recorddata - The record data.
 * @returns {object} - The action object with type 'RECORD_DATA', recordidinfo, and recorddata.
 */
export const setRecordData = (recordidinfo, recorddata) => {
    return {
        type: RECORD_DATA,
        recordidinfo: recordidinfo,
        recorddata: { ...recorddata }
    }
}

/**
 * Redux action function to set the selectedGridRows data.
 * @param {object} selectedGridRows - The selectedGridRows data.
 * @returns {object} - The action object with type 'SELECTED_GRID_ROWS', selectedGridRows, and selectedGridRowsdata.
 */
export const setSelectedGridRows = (selectedGridRows) => {
    return {
        type: SELECTED_GRID_ROWS,
        selectedGridRows
    }
}

/**
 * Redux action function to set the notifications.
 * @param {object} data - The notifications data.
 * @returns {function} - The thunk function to dispatch the action.
 */
export const setNotifications = (data = null) => {
    return (dispatch) => {
        let url = getEnvVariable('Report') + `/api/ReportQuery/DashboardNotifications`
        if (getEnvVariable('ModuleKey') === 'AMES')
            url = getEnvVariable('Report') + `/api/ReportQuery/DashboardNotificationsCount`
        const body = { "IsRead": 'N', "Dismissed": 'N' }
        PostData(url, body, true).then(response => {
            if (getEnvVariable('ModuleKey') === 'AMES') {
                if (response && response.totalCount) {
                    dispatch(setNotify(response.totalCount))
                }
            }
            else {
                if (response && response.value) {
                    localStorage.setItem('notificationCount', response.value.total)
                    dispatch(setNotify(response.value.total))
                }
            }
        }).catch(ex => {
            console.log(ex)
        })

    }
}

/**
 * Redux action function to get project messages.
 * @returns {function} - The thunk function to dispatch the action.
 */
export const getProjectMessages = () => {
    return (dispatch) => {
        let url = getEnvVariable('Application') + '/api/ProjectMessages/All';
        const init = {
            method: 'GET', ...GetFetchProps()
        };
        fetch(`${url}`, init).then(response => response.json()).then(response => {
            if (response) {
                dispatch(getProjMessagesfromService(response))
            }
        }).catch(ex => {
            console.log(ex)
        })
    }
}

/**
 * Redux action function to handle passtab data.
 * @param {object} passtabdata - The passtab data.
 * @param {object} initialdata - The initial data.
 * @returns {object} - The action object with type 'PASSTAB_DATA', passtabdata, and initialdata.
 */
export const passtabData = (passtabdata, initialdata) => {
    return {
        type: PASSTAB_DATA,
        passtabdata: { ...passtabdata },
        initialdata: { ...initialdata }
    }
}

/**
 * Redux action function to trigger after save.
 * @param {any} onTriggerSave - The value to trigger after save.
 * @returns {object} - The action object with type 'TRIGGER_AFTER_SAVE' and onTriggerSave.
 */
export const onTirggerAfterSave = (onTriggerSave = null) => {
    return {
        type: TRIGGER_AFTER_SAVE,
        onTriggerSave: onTriggerSave
    }
}

/**
 * Redux action function to open the left menu drawer.
 * @param {boolean} leftmenuDrawer - Whether the left menu drawer is open.
 * @returns {object} - The action object with type 'LEFTMENUDRAWER' and leftmenuDrawer.
 */
export const leftmenuDrawerOpen = (leftmenuDrawer = false) => {
    return {
        type: LEFTMENUDRAWER,
        leftmenuDrawer: leftmenuDrawer
    }
}

/**
 * Redux action function to move the sidebar to top.
 * @param {string} movebartoTop - Whether to move the sidebar to top.
 * @returns {object} - The action object with type 'MOVEBARTOTOP' and movebartoTop.
 */
export const moveSidebartoTop = (movebartoTop = 'Y') => {
    return {
        type: MOVEBARTOTOP,
        movebartoTop: movebartoTop
    }
}